// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Building2, 
  Calendar, 
  TrendingUp, 
  Settings, 
  DollarSign,
  Users,
  CheckCircle,
  AlertCircle,
  Clock
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface ActivePartnership {
  id: string;
  partnership_name: string;
  partnership_type: string;
  contract_start_date: string;
  contract_end_date: string;
  revenue_sharing_percentage: number;
  integration_status: string;
  monthly_revenue_generated: number;
  partnership_health_score: number;
  renewal_probability: string;
  partner_prospects: {
    company_name: string;
    contact_name: string;
    contact_email: string;
  };
}

interface ActivePartnershipsProps {
  searchTerm: string;
}

const INTEGRATION_STATUS = {
  'planning': { label: 'Planning', color: 'bg-gray-100 text-gray-800', icon: Clock },
  'in_progress': { label: 'In Progress', color: 'bg-blue-100 text-blue-800', icon: Settings },
  'testing': { label: 'Testing', color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
  'completed': { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  'maintenance': { label: 'Maintenance', color: 'bg-purple-100 text-purple-800', icon: Settings }
};

const RENEWAL_PROBABILITY = {
  'high': { label: 'High', color: 'text-green-600' },
  'medium': { label: 'Medium', color: 'text-yellow-600' },
  'low': { label: 'Low', color: 'text-red-600' }
};

const ActivePartnerships: React.FC<ActivePartnershipsProps> = ({ searchTerm }) => {
  const [partnerships, setPartnerships] = useState<ActivePartnership[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivePartnerships();
  }, []);

  const fetchActivePartnerships = async () => {
    try {
      const { data, error } = await supabase
        .from('active_partnerships')
        .select(`
          *,
          partner_prospects (
            company_name,
            contact_name,
            contact_email
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPartnerships(data || []);
    } catch (error) {
      console.error('Error fetching active partnerships:', error);
      toast.error('Failed to load active partnerships');
    } finally {
      setLoading(false);
    }
  };

  const filteredPartnerships = partnerships.filter(partnership => {
    return searchTerm === '' || 
      partnership.partnership_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partnership.partner_prospects?.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partnership.partnership_type.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getHealthScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getDaysUntilRenewal = (endDate: string) => {
    const today = new Date();
    const renewal = new Date(endDate);
    const diffTime = renewal.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-80 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Total Active</p>
                <p className="text-xl font-bold">{partnerships.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm text-muted-foreground">Monthly Revenue</p>
                <p className="text-xl font-bold">
                  {formatCurrency(partnerships.reduce((sum, p) => sum + (p.monthly_revenue_generated || 0), 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm text-muted-foreground">Integrations</p>
                <p className="text-xl font-bold">
                  {partnerships.filter(p => p.integration_status === 'completed').length}/{partnerships.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm text-muted-foreground">Avg Health</p>
                <p className="text-xl font-bold">
                  {partnerships.length > 0 
                    ? (partnerships.reduce((sum, p) => sum + (p.partnership_health_score || 0), 0) / partnerships.length).toFixed(1)
                    : '0'
                  }/10
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Partnerships Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPartnerships.map((partnership) => {
          const integrationConfig = INTEGRATION_STATUS[partnership.integration_status as keyof typeof INTEGRATION_STATUS];
          const renewalConfig = RENEWAL_PROBABILITY[partnership.renewal_probability as keyof typeof RENEWAL_PROBABILITY];
          const daysUntilRenewal = getDaysUntilRenewal(partnership.contract_end_date);
          const IntegrationIcon = integrationConfig?.icon || Clock;

          return (
            <Card key={partnership.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{partnership.partnership_name}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {partnership.partner_prospects?.company_name}
                    </p>
                  </div>
                  <Badge variant="outline" className="capitalize">
                    {partnership.partnership_type}
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Integration Status */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <IntegrationIcon className="h-4 w-4" />
                    <span className="text-sm">Integration</span>
                  </div>
                  <Badge className={integrationConfig?.color}>
                    {integrationConfig?.label}
                  </Badge>
                </div>

                {/* Revenue & Health */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Monthly Revenue</p>
                    <p className="text-lg font-semibold text-green-600">
                      {formatCurrency(partnership.monthly_revenue_generated || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Health Score</p>
                    <p className={`text-lg font-semibold ${getHealthScoreColor(partnership.partnership_health_score || 0)}`}>
                      {partnership.partnership_health_score || 0}/10
                    </p>
                  </div>
                </div>

                {/* Contract Timeline */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Contract Period</span>
                    <span>{daysUntilRenewal} days until renewal</span>
                  </div>
                  <div className="text-xs">
                    <span>{formatDate(partnership.contract_start_date)} - {formatDate(partnership.contract_end_date)}</span>
                  </div>
                  <Progress 
                    value={Math.max(0, Math.min(100, 100 - (daysUntilRenewal / 365 * 100)))}
                    className="h-2"
                  />
                </div>

                {/* Revenue Sharing */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Revenue Share</span>
                  <span className="text-sm font-medium">{partnership.revenue_sharing_percentage || 0}%</span>
                </div>

                {/* Renewal Probability */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Renewal Probability</span>
                  <span className={`text-sm font-medium ${renewalConfig?.color}`}>
                    {renewalConfig?.label}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" className="flex-1">
                    <Settings className="h-3 w-3 mr-1" />
                    Manage
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Calendar className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredPartnerships.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No active partnerships found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm ? 'No partnerships match your search criteria.' : 'No active partnerships to display.'}
            </p>
            <Button onClick={() => window.location.href = '/sales-dashboard/partners?tab=pipeline'}>
              View Pipeline
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ActivePartnerships;